import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { CheckCircle, ArrowRight, Phone, Mail, Clock } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import { CONTACT } from "@/lib/constants";

export default function Tack() {
  const [, navigate] = useLocation();

  // If someone navigates directly to /tack without submitting the form,
  // redirect them to the contact page after a short delay
  useEffect(() => {
    const referrer = document.referrer;
    const isFromContact =
      referrer.includes("/kontakt") ||
      sessionStorage.getItem("contact_form_submitted") === "true";

    if (!isFromContact) {
      navigate("/kontakt");
    }

    // Clean up the session flag after showing the page
    return () => {
      sessionStorage.removeItem("contact_form_submitted");
    };
  }, [navigate]);

  // Set noindex meta tag to prevent search engines from indexing this page
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");

    return () => {
      meta?.remove();
    };
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Tack för din förfrågan | ASPAB"
        description="Tack för att du kontaktade ASPAB. Vi återkommer inom 24 timmar."
        path="/tack"
        noindex
      />

      <section className="bg-white py-20 md:py-32">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection>
              {/* Success icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-50 border-2 border-green-200 rounded-full mb-8">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>

              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
                Tack för din förfrågan!
              </h1>

              <p className="text-gray-500 text-lg mt-4 leading-relaxed max-w-lg mx-auto">
                Vi har tagit emot ditt meddelande och återkommer till dig inom 24 timmar 
                med en offert eller svar på din fråga.
              </p>
            </AnimatedSection>

            {/* What happens next */}
            <AnimatedSection delay={200}>
              <div className="mt-12 bg-gray-50 border border-gray-200 rounded-xl p-8 text-left">
                <h2 className="font-heading text-xl font-semibold text-gray-900 mb-6">
                  Vad händer nu?
                </h2>
                <div className="space-y-5">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#2563EB] text-white text-sm font-bold rounded-full">
                      1
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">Vi granskar din förfrågan</h3>
                      <p className="text-gray-500 text-sm mt-0.5">
                        Vårt team går igenom ditt meddelande och bedömer materialbehov, toleranser och leveranstid.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#2563EB] text-white text-sm font-bold rounded-full">
                      2
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">Vi kontaktar dig</h3>
                      <p className="text-gray-500 text-sm mt-0.5">
                        Inom 24 timmar hör vi av oss via e-post eller telefon med en offert eller eventuella följdfrågor.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#2563EB] text-white text-sm font-bold rounded-full">
                      3
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-medium">Produktion startar</h3>
                      <p className="text-gray-500 text-sm mt-0.5">
                        Efter godkänd offert påbörjar vi tillverkning. Enkla detaljer levereras ofta samma dag eller dagen efter.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Quick contact info */}
            <AnimatedSection delay={400}>
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <a
                  href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                  className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#2563EB]/30 hover:shadow-sm transition-all group"
                >
                  <Phone className="w-5 h-5 text-[#2563EB]" />
                  <div className="text-left">
                    <div className="text-gray-500 text-xs">Ring oss</div>
                    <div className="text-gray-900 text-sm font-medium group-hover:text-[#2563EB] transition-colors">
                      {CONTACT.phone}
                    </div>
                  </div>
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#2563EB]/30 hover:shadow-sm transition-all group"
                >
                  <Mail className="w-5 h-5 text-[#2563EB]" />
                  <div className="text-left">
                    <div className="text-gray-500 text-xs">E-post</div>
                    <div className="text-gray-900 text-sm font-medium group-hover:text-[#2563EB] transition-colors">
                      {CONTACT.email}
                    </div>
                  </div>
                </a>
                <div className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg">
                  <Clock className="w-5 h-5 text-[#2563EB]" />
                  <div className="text-left">
                    <div className="text-gray-500 text-xs">Svarstid</div>
                    <div className="text-gray-900 text-sm font-medium">Inom 24 timmar</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Navigation links */}
            <AnimatedSection delay={600}>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  href="/"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors"
                >
                  Tillbaka till startsidan
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/tjanster"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white border border-gray-200 text-gray-700 font-medium rounded-md hover:border-[#2563EB]/30 hover:text-[#2563EB] transition-colors"
                >
                  Utforska våra tjänster
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </Layout>
  );
}
